/**
 * Parent email verification placement — GrowthBook WHEN only (sc-222848).
 *
 * Flag `sc-parent-email-verification-placement`:
 * - `parent_onboarding`          → hard-gate after employer eligibility, before care-type
 * - `dashboard`                  → hard-gate: verify-email before pricing (sc-228321) and
 *                                  before parent dashboard
 * - `dashboard_soft_gate`        → soft-gate: banner + blurred dashboard sections
 * - `no_verification_required`   → control — never force EV for this experiment
 *
 * Kept separate from the sitter placementPolicy so the two experiments cannot collide.
 *
 * Cohort: only members with `email_verification_required = true` (new-user hard-gate)
 * enter this experiment. Existing parents (`false`) get GraphQL `showBanner` only.
 *
 * Safe fallback: unknown values → `dashboard_soft_gate`.
 */
import { getFeatureValue, syncGrowthbookIdentity } from '@/lib/growthbook';

/** Hard-gate after employer eligibility, before care-needs onboarding. */
export const PARENT_PLACEMENT_ONBOARDING = 'parent_onboarding';

/** Hard-gate: show /verify-email before pricing and before the parent dashboard. */
export const PARENT_PLACEMENT_DASHBOARD = 'dashboard';

/** Soft-gate on the parent dashboard (banner + selective blur). Also the safe default for unknowns. */
export const PARENT_PLACEMENT_DASHBOARD_SOFT_GATE = 'dashboard_soft_gate';

/** Do not force email verification (experiment control). */
export const PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED = 'no_verification_required';

/** GrowthBook feature key — keep in sync with the Sittercity GB project. */
export const PARENT_EMAIL_VERIFICATION_PLACEMENT_FEATURE_KEY = 'sc-parent-email-verification-placement';

export type KnownParentEmailVerificationPlacement =
  | typeof PARENT_PLACEMENT_ONBOARDING
  | typeof PARENT_PLACEMENT_DASHBOARD
  | typeof PARENT_PLACEMENT_DASHBOARD_SOFT_GATE
  | typeof PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED;

export type ParentEmailVerificationPlacement = KnownParentEmailVerificationPlacement | string;

export type ParentEmailVerificationMoment = 'onboarding' | 'pricing' | 'dashboard';

/**
 * Variant A resumes here after verification (employer eligibility → EV → care-type).
 * Keep in sync with sittercity VerificationController::PARENT_ONBOARDING_CONTINUE_PATH.
 */
export const PARENT_ONBOARDING_CONTINUE_PATH = '/registration/care_type';

/**
 * Employer-eligibility (and location) steps that must complete before Variant A EV.
 * Global re-entry gating skips these so parents are not forced into EV too early.
 */
const PARENT_PRE_EMAIL_VERIFICATION_PATHS = new Set([
  '/registration',
  '/registration/',
  '/registration/location',
  '/registration/add_employer',
  '/registration/request_upgrade',
  '/registration/upgrade_request_sent',
]);

/** True for pre-EV parent registration paths (employer eligibility / location). */
export function isParentPreEmailVerificationPath(path: string): boolean {
  const pathname = path.split(/[?#]/)[0];
  return PARENT_PRE_EMAIL_VERIFICATION_PATHS.has(pathname);
}

/**
 * Steps a dashboard-placement parent may still open before email verification.
 * Job post and registration happen before the pricing/dashboard hard-gate.
 * Product surfaces (jobs list, messages, search, invites) are not included.
 */
const PARENT_DASHBOARD_EXEMPT_PREFIXES = [
  '/registration',
  '/job-post',
  '/onboarding/seeker',
  '/corporate-onboarding',
  '/account/deactivation',
  '/account/seeker/photo',
];

export function isParentDashboardEmailVerificationExemptPath(path: string): boolean {
  const pathname = path.split(/[?#]/)[0];
  return PARENT_DASHBOARD_EXEMPT_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/** Hard = redirect to /verify-email. Soft = banner / dashboard blur only. */
export type ParentEmailVerificationRestriction = 'hard' | 'soft';

export type ParentEmailVerificationPolicyInput = {
  verified: boolean;
  isParent: boolean;
  isCorporate?: boolean;
  /** New-user hard-gate cohort. Existing users (`false`) never enter experiment UX. */
  emailVerificationRequired: boolean;
  placement: ParentEmailVerificationPlacement;
  moment: ParentEmailVerificationMoment;
  /** Defaults to `hard` for redirect gates; pass `soft` for banner/blur. */
  restriction?: ParentEmailVerificationRestriction;
};

export function normalizeParentEmailVerificationPlacement(
  placement: ParentEmailVerificationPlacement
): KnownParentEmailVerificationPlacement {
  if (placement === PARENT_PLACEMENT_ONBOARDING) {
    return PARENT_PLACEMENT_ONBOARDING;
  }

  if (placement === PARENT_PLACEMENT_DASHBOARD) {
    return PARENT_PLACEMENT_DASHBOARD;
  }

  if (placement === PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED;
  }

  if (placement === PARENT_PLACEMENT_DASHBOARD_SOFT_GATE) {
    return PARENT_PLACEMENT_DASHBOARD_SOFT_GATE;
  }

  return PARENT_PLACEMENT_DASHBOARD_SOFT_GATE;
}

export function getParentEmailVerificationPlacement(): KnownParentEmailVerificationPlacement {
  syncGrowthbookIdentity();
  const raw = getFeatureValue(PARENT_EMAIL_VERIFICATION_PLACEMENT_FEATURE_KEY, PARENT_PLACEMENT_DASHBOARD_SOFT_GATE);
  return normalizeParentEmailVerificationPlacement(raw);
}

/**
 * Pure placement policy for the parent experiment:
 * - existing soft-banner cohort / verified / non-parent / corporate / control → never
 * - onboarding + hard: `parent_onboarding` only
 * - pricing + hard: `dashboard` only (Variant B — EV before purchase page)
 * - dashboard + hard: `dashboard` only
 * - dashboard + soft: `dashboard_soft_gate` only (no blur fallback for other placements)
 */
export function shouldRequireParentEmailVerificationNow(input: ParentEmailVerificationPolicyInput): boolean {
  if (input.verified || !input.isParent || input.isCorporate || !input.emailVerificationRequired) {
    return false;
  }

  const placement = normalizeParentEmailVerificationPlacement(input.placement);
  if (placement === PARENT_PLACEMENT_NO_VERIFICATION_REQUIRED) {
    return false;
  }

  const restriction = input.restriction ?? 'hard';

  if (input.moment === 'onboarding') {
    return restriction === 'hard' && placement === PARENT_PLACEMENT_ONBOARDING;
  }

  if (input.moment === 'pricing') {
    return restriction === 'hard' && placement === PARENT_PLACEMENT_DASHBOARD;
  }

  if (restriction === 'hard') {
    return placement === PARENT_PLACEMENT_DASHBOARD;
  }

  return placement === PARENT_PLACEMENT_DASHBOARD_SOFT_GATE;
}
